import React from 'react';
import { SEO } from '../components/seo/SEO';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import styles from './InsightsIndex.module.css';

const content = {
  de: {
    seo: {
      title: 'Executive Resource Center',
      description:
        'Strategische Briefings für CFO, CISO und CIO: Governance, SAP-Modernisierung, Data Quality und DORA/NIS2.',
    },
    hero: {
      eyebrow: 'Executive Resource Center',
      title: 'Strategische Briefings für Entscheider',
      subtitle:
        'Persona-spezifische Briefings für CFO, CISO und CIO – kompakt, DACH-relevant und procurement-ready.',
    },
    cta: 'Briefing lesen →',
    sections: [
      {
        title: 'Für CFOs: OpEx & Portfolio-Steuerung',
        description: 'Kostenstabilität, Governance und belastbare Entscheidungsgrundlagen.',
        posts: [
          {
            title: 'Data Quality als Grundlage für belastbare Forecasts',
            desc: 'Wie Sie Datenqualität als Governance-Programm etablieren.',
            href: '/insights/data-quality-ai',
            tag: 'Data Strategy',
            image: '/images/cloud_platform_hero.png',
          },
          {
            title: 'CSRD/ESG Tech Stack',
            desc: 'Datenpipelines, Dashboards und Audit Trails für ESG-Reporting.',
            href: '/insights/csrd-esg-tech',
            tag: 'ESG Tech',
            image: '/images/sustainability_esg_hero.png',
          },
        ],
      },
      {
        title: 'Für CISOs: Governance & Resilience',
        description: 'NIS2/DORA Readiness, Evidence Packs und Audit-Standards.',
        posts: [
          {
            title: 'NIS2 & GDPR für AI Workflows',
            desc: 'Controls, Logging, Audit Trails und procurement-ready Evidence.',
            href: '/insights/nis2-gdpr-ai',
            tag: 'Security & Compliance',
            image: '/images/security_governance_hero.png',
          },
          {
            title: 'AI Governance & LLMOps: EU-ready, production-first',
            desc: 'Guardrails, Observability, EU AI Act Awareness und SAP/ERP Integration.',
            href: '/insights/ai-governance',
            tag: 'AI Governance',
            image: '/images/ai_automation_hero.png',
          },
        ],
      },
      {
        title: 'Für CIOs: Modernisierung & Delivery',
        description: 'SAP/ERP-Transformation und AI-Integration im Betrieb.',
        posts: [
          {
            title: 'SAP + AI Modernisierung',
            desc: 'Wie Sie SAP/ERP mit AI Assistants, Transports und Fiori Delivery verbinden.',
            href: '/insights/sap-ai-modernization',
            tag: 'SAP & AI',
            image: '/images/erp_sap_hero.png',
          },
        ],
      },
    ],
  },
  en: {
    seo: {
      title: 'Executive Resource Center',
      description:
        'Strategic briefs for CFOs, CISOs, and CIOs: governance, SAP modernization, data quality, and DORA/NIS2.',
    },
    hero: {
      eyebrow: 'Executive Resource Center',
      title: 'Strategic briefs for decision-makers',
      subtitle:
        'Persona-specific briefs for CFOs, CISOs, and CIOs – concise, DACH-relevant, and procurement-ready.',
    },
    cta: 'Read the brief →',
    sections: [
      {
        title: 'For CFOs: OpEx & portfolio control',
        description: 'Cost stability, governance, and decision-ready baselines.',
        posts: [
          {
            title: 'Data quality as the foundation for reliable forecasts',
            desc: 'How to establish data quality as a governance program.',
            href: '/insights/data-quality-ai',
            tag: 'Data Strategy',
            image: '/images/cloud_platform_hero.png',
          },
          {
            title: 'CSRD/ESG tech stack',
            desc: 'Data pipelines, dashboards, and audit trails for ESG reporting.',
            href: '/insights/csrd-esg-tech',
            tag: 'ESG Tech',
            image: '/images/sustainability_esg_hero.png',
          },
        ],
      },
      {
        title: 'For CISOs: Governance & resilience',
        description: 'NIS2/DORA readiness, evidence packs, and audit standards.',
        posts: [
          {
            title: 'NIS2 & GDPR for AI workflows',
            desc: 'Controls, logging, audit trails, and procurement-ready evidence.',
            href: '/insights/nis2-gdpr-ai',
            tag: 'Security & Compliance',
            image: '/images/security_governance_hero.png',
          },
          {
            title: 'AI Governance & LLMOps: EU-ready, production-first',
            desc: 'Guardrails, observability, EU AI Act awareness, and SAP/ERP integration.',
            href: '/insights/ai-governance',
            tag: 'AI Governance',
            image: '/images/ai_automation_hero.png',
          },
        ],
      },
      {
        title: 'For CIOs: Modernization & delivery',
        description: 'SAP/ERP transformation and operating AI in production.',
        posts: [
          {
            title: 'SAP + AI modernization',
            desc: 'How to align SAP/ERP with AI assistants, transports, and Fiori delivery.',
            href: '/insights/sap-ai-modernization',
            tag: 'SAP & AI',
            image: '/images/erp_sap_hero.png',
          },
        ],
      },
    ],
  },
};

export const InsightsIndex: React.FC = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <div className={styles.page}>
      <SEO title={t.seo.title} description={t.seo.description} canonical="/insights" />
      <section className={styles.hero}>
        <div className={styles.hero__container}>
          <span className={styles.hero__eyebrow}>{t.hero.eyebrow}</span>
          <h1 className={styles.hero__title}>{t.hero.title}</h1>
          <p className={styles.hero__subtitle}>{t.hero.subtitle}</p>
        </div>
      </section>

      <section className={styles.list}>
        <div className={styles.list__container}>
          {t.sections.map((section) => (
            <div key={section.title} className={styles.section}>
              <div className={styles.section__header}>
                <h2 className={styles.section__title}>{section.title}</h2>
                <p className={styles.section__desc}>{section.description}</p>
              </div>
              <div className={styles.section__grid}>
                {section.posts.map((post) => (
                  <div key={post.href} className={styles.card}>
                    <div className={styles.card__media}>
                      <picture>
                        <source
                          type="image/webp"
                          srcSet={`${post.image.replace(/\.(png|jpg|jpeg)$/i, '')}-800.webp 800w, ${post.image.replace(/\.(png|jpg|jpeg)$/i, '')}-1600.webp 1600w`}
                          sizes="(max-width: 768px) 100vw, 260px"
                        />
                        <img src={post.image} alt={post.title} loading="lazy" />
                      </picture>
                    </div>
                    <div className={styles.card__content}>
                      <span className={styles.card__tag}>{post.tag}</span>
                      <h3 className={styles.card__title}>{post.title}</h3>
                      <p className={styles.card__desc}>{post.desc}</p>
                      <Link to={post.href} className={styles.card__link}>
                        {t.cta}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default InsightsIndex;
